<template>
  <section v-if="loading" class="tools-library">
    <p-loading />
  </section>

  <section v-else class="tools-library">
    <header>
      <h1>Tools Library</h1>
    </header>

    <section>
      <p-list
        name="tools"
        :data="filteredTools"
        :total="filteredTools.length !== tools.length ? filteredTools.length : tools.length"
        :metadata="{
          name: { label: 'Name', width: 'minmax(0, 2fr)', sortable: true },
          description: { label: 'Description', width: 'minmax(0, 4fr)', sortable: true },
          tags: { label: 'Tags', width: 'minmax(125px, 1fr)', sortable: false },
          organizations: { label: 'Organizations', width: 'minmax(125px, 1fr)', sortable: false },
          lastModified: { label: 'Last Modified', width: '150px', sortable: true }
        }"
        @edit="edit"
      >
        <template #name="{ value, item }">
          <router-link :to="`/tools/${item.name}/edit`" class="tool-name">
            <strong>{{ value }}</strong>
          </router-link>
        </template>
        <template #description="{ value }">
          <div class="tool-description">{{ value }}</div>
        </template>
        <template #tags="{ value }">
          <div v-if="value && value.length" class="tool-tags">
            <span v-for="tag in value" :key="tag" class="tag">{{ tag }}</span>
          </div>
        </template>
        <template #organizations="{ value }">
          <div v-if="value && value.length" class="tool-tags">
            <span v-for="orgId in value" :key="orgId" class="tag">{{ getOrganizationName(orgId) }}</span>
          </div>
        </template>
        <template #lastModified="{ value, item }">
          <div class="last-modified-cell">
            <div>{{ formatDate(value) }}</div>
            <div class="modified-by">by {{ item.lastModifiedBy || 'Unknown' }}</div>
          </div>
        </template>
        <template #__actions="{ item }">
          <p-button variant="text" title="Edit this tool" @click="edit(item.name)"> &#9998;</p-button>
        </template>
        <template slot="filter">
          <div class="tags-wrapper">
            <Multiselect v-model="selectedTags" :multiple="true" placeholder="Filter by tag..." :options="allTags" />
            <Multiselect v-model="selectedOrganizations" :multiple="true" placeholder="Filter by organization..." :options="organizationsOptions" />
          </div>
        </template>
      </p-list>
    </section>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import Button from '@/components/common/Button';
import Loading from '@/components/common/Loading';
import List from '@/components/common/List';
import Multiselect from '@/components/common/Multiselect';

export default {
  components: {
    'p-loading': Loading,
    'p-list': List,
    'p-button': Button,
    Multiselect
  },
  data() {
    return {
      selectedTags: [],
      selectedOrganizations: []
    };
  },
  computed: {
    ...mapState({
      loading: s => s.nlp.configurationsPending,
      tools: s => s.nlp.tools,
      organizationsCollection: s => s.organizations.collection
    }),
    allTags() {
      const set = this.tools.reduce((acc, item) => {
        item.tags && item.tags.forEach(element => acc.add(element));
        return acc;
      }, new Set());
      return Array.from(set);
    },
    organizationsOptions() {
      return this.organizationsCollection.map(org => org.name);
    },
    organizationsMapping() {
      return this.organizationsCollection.reduce((acc, org) => {
        acc[org.id] = org.name;
        return acc;
      }, {});
    },
    filteredTools() {
      return this.tools
        .filter(tool => {
          if (this.selectedOrganizations?.length > 0) {
            if (!tool.organizations) {
              return false;
            }
            if (tool.organizations.length === 0) {
              return false;
            }
            const orgNames = tool.organizations.map(id => this.organizationsMapping[id]);
            const organizationsIntersecting = this.selectedOrganizations.some(org => orgNames.includes(org));
            if (!organizationsIntersecting) {
              return false;
            }
          }
          if (this.selectedTags.length !== 0) {
            if (!this.selectedTags.some(tag => tool.tags?.includes(tag))) {
              return false;
            }
          }
          return true;
        })
        .map(tool => ({
          ...tool,
          id: tool.name
        }));
    }
  },
  async created() {
    await Promise.all([this.$store.dispatch('nlp/getConfigurations'), this.$store.dispatch('organizations/getCollection')]);
  },
  methods: {
    getOrganizationName(id) {
      return this.organizationsCollection.find(o => o.id === id)?.name ?? 'Unknown Organization';
    },
    formatDate(value) {
      if (!value) return '';
      try {
        const date = new Date(value);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return value;
      }
    },
    async edit(name) {
      this.$router.push({ path: `/tools/${name}/edit` });
    }
  }
};
</script>
<style scoped lang="scss">
.tools-library {
  display: grid;
  padding: 0 0.5rem 0 1rem;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  grid-gap: 0.5rem;
  width: 100%;
  height: 100%;

  .tags-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 5px;
    align-items: center;
  }

  > header {
    padding: 0.5rem 0.5rem 0;
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;

    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
      margin-right: 0.25rem;
    }

    .action-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }

  .tool-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;

    .tag {
      display: inline-flex;
      align-items: center;
      background-color: var(--theme-primary);
      border-radius: 2px;
      border: 1px solid var(--theme-primary);
      color: var(--theme-on-primary);
      padding: 2px 6px;
      font-size: 11px;
      white-space: nowrap;
    }
  }

  .tool-name {
    color: var(--theme-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .tool-description {
    line-height: 1.6;
  }

  .last-modified-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .modified-by {
      font-size: 0.85em;
      color: var(--theme-on-surface-variant);
    }
  }
}
</style>
